/**
 * User Profile React Query Hooks
 *
 * Hooks for fetching and updating user profile and settings.
 */

import type { UserSettings } from '@luminaria/shared';
import { queryOptions, useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { authApi, type CurrentUser } from '../api/authApi';
import { invalidate } from '../api/invalidation';
import { userKeys } from '../api/queryKeys';

/**
 * Store the server's saved profile, then mark everything that depends on
 * settings stale WITHOUT refetching it (WEBD-01). Writing the profile is what
 * refreshes the tax calculation: its key carries the request body and the
 * saved settings, so the next render asks for a new key and fetches once,
 * with the new values. An active refetch here would instead re-run the old
 * key synchronously, before React has re-rendered — a second, wasted request
 * for numbers nobody shows.
 */
function applySavedProfile(queryClient: QueryClient, updatedUser: CurrentUser): void {
  queryClient.setQueryData(userKeys.profile(), updatedUser);
  void invalidate(queryClient, 'settings', { refetchType: 'none' });
}

/**
 * The one definition of the current-user query. AuthProvider warms it with
 * `ensureQueryData` when a session starts; every reader uses the hooks below
 * (WEBD-24), so there is one cache entry for the user.
 */
export const userProfileQueryOptions = queryOptions({
  queryKey: userKeys.profile(),
  queryFn: () => authApi.me(),
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes
});

/**
 * Fetch current user profile
 */
export function useUserProfile() {
  return useQuery(userProfileQueryOptions);
}

/** The signed-in user's profile (tier, admin flag, settings); undefined until loaded. */
export function useCurrentUser(): CurrentUser | undefined {
  return useUserProfile().data;
}

/**
 * Update the user's settings
 */
export function useUpdateUserSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<UserSettings>) => authApi.updateMe({ settings }),
    onSuccess: (updatedUser) => {
      applySavedProfile(queryClient, updatedUser);
    },
  });
}
